import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageIcon, KeyRound, ShieldCheck } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDateTime } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { ConfiguredBadge, LockedCredentialNotice } from '../../components/credential-lock'
import { useEditGate } from '../../components/credential-lock.lib'
import { MagnificTestPanel } from './MagnificTestPanel'
import { CatalogStudioRecipeCard } from './CatalogStudioRecipeCard'
import { useT } from '../../i18n'
import { mediaGenerationI18n } from '../../i18n/admin/media-generation'

type MediaGenerationCredentialRead = {
  id: string
  provider: 'magnific'
  display_name: string
  api_key_hint: string | null
  has_api_key: boolean
  active: boolean
  last_validated_at: string | null
  created_at: string
  updated_at: string
}

type MediaGenerationProviderStatus = {
  provider: 'magnific'
  configured: boolean
  source: 'db' | 'env' | 'none'
}

type MediaGenerationCredentials = {
  items: MediaGenerationCredentialRead[]
  statuses: MediaGenerationProviderStatus[]
}

const QUERY_KEY = ['admin-media-generation-credentials'] as const
const INPUT_CLASS =
  'w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] font-mono text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

async function fetchCredentials(): Promise<MediaGenerationCredentials> {
  const response = await fetch(apiUrl('/admin/media-generation-credentials'))
  if (!response.ok) throw new Error(`fetch media generation credentials ${response.status}`)
  return (await response.json()) as MediaGenerationCredentials
}

function errorMessage(body: unknown, fallback: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'detail' in body &&
    body.detail &&
    typeof body.detail === 'object' &&
    'message' in body.detail &&
    typeof body.detail.message === 'string'
  ) {
    return body.detail.message
  }
  return fallback
}

async function requestCredential(
  path: string,
  init: RequestInit,
): Promise<MediaGenerationCredentialRead | null> {
  const response = await fetch(apiUrl(path), init)
  if (!response.ok) {
    throw new Error(errorMessage(await response.json().catch(() => null), `request failed (${response.status})`))
  }
  return response.status === 204 ? null : ((await response.json()) as MediaGenerationCredentialRead)
}

export function MediaGeneration() {
  const t = useT(mediaGenerationI18n)
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchCredentials })
  const current = query.data?.items.find((item) => item.provider === 'magnific') ?? null
  const status = query.data?.statuses.find((item) => item.provider === 'magnific')

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-[var(--color-text)]">
          <ImageIcon size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
          <ShieldCheck size={12} className="text-[var(--color-success)]" aria-hidden="true" />
          {t.encryptedNote}
        </p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] p-3 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
          {t.simulationNotice}
        </div>
      )}

      {query.isLoading ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
          {t.loading}
        </div>
      ) : query.error ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-danger)] ring-1 ring-[var(--color-border)]">
          {t.loadError}
        </div>
      ) : (
        <MagnificCard
          current={current}
          configured={Boolean(status?.configured)}
          sourceLabel={status && status.source !== 'none' ? t.sourceLabels[status.source] : null}
          onChanged={() => queryClient.invalidateQueries({ queryKey: QUERY_KEY })}
        />
      )}

      <CatalogStudioRecipeCard />

      {!query.isLoading && !query.error && (
        <MagnificTestPanel providerReady={Boolean(status?.configured && (!current || current.active))} />
      )}
    </div>
  )
}

function MagnificCard({
  current,
  configured,
  sourceLabel,
  onChanged,
}: {
  current: MediaGenerationCredentialRead | null
  configured: boolean
  sourceLabel: string | null
  onChanged: () => void
}) {
  const t = useT(mediaGenerationI18n)
  const gate = useEditGate(configured)
  const [apiKey, setApiKey] = useState('')
  const [active, setActive] = useState(current?.active ?? true)
  const beginEdit = () => {
    setActive(current?.active ?? true)
    gate.requestEdit()
  }

  const save = useMutation({
    mutationFn: () =>
      requestCredential('/admin/media-generation-credentials/magnific', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: 'Magnific', api_key: apiKey.trim(), active }),
      }),
    onSuccess: () => {
      setApiKey('')
      gate.lock()
      onChanged()
      toast.success(t.savedToast, {
        description: t.savedToastDescription,
        audience: 'admin',
      })
    },
    onError: (error: unknown) => {
      toast.error(t.saveErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const deactivate = useMutation({
    mutationFn: () =>
      requestCredential('/admin/media-generation-credentials/magnific/deactivate', {
        method: 'POST',
      }),
    onSuccess: () => {
      onChanged()
      toast.success(t.deactivatedToast, { audience: 'admin' })
    },
    onError: (error: unknown) => {
      toast.error(t.deactivateErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const remove = useMutation({
    mutationFn: () =>
      requestCredential('/admin/media-generation-credentials/magnific', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      onChanged()
      toast.success(t.removedToast, { audience: 'admin' })
    },
    onError: (error: unknown) => {
      toast.error(t.removeErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const canSave = USE_REAL_API && apiKey.trim().length > 0 && !save.isPending

  return (
    <section className="overflow-hidden rounded-lg bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]" aria-label="Magnific">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-info)]">
            <ImageIcon size={18} aria-hidden="true" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[var(--color-text)]">Magnific</div>
            <div className="text-[11px] text-[var(--color-text-muted)]">{t.providerSubtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {current && <ActiveBadge active={current.active} />}
          <ConfiguredBadge configured={configured} sourceLabel={sourceLabel} />
        </div>
      </div>

      {!gate.editing ? (
        <div className="space-y-3 px-5 py-5">
          <LockedCredentialNotice
            hint={current?.api_key_hint}
            onEdit={beginEdit}
            label={t.credentialsLabel}
          />
          {current && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[var(--color-text-muted)]">
              <span>{t.updated(formatDateTime(current.updated_at))}</span>
              <button
                type="button"
                disabled={!current.active || deactivate.isPending}
                onClick={() => deactivate.mutate()}
                className="font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t.deactivate}
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
                className="font-medium text-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t.remove}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4 px-5 py-5">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.apiKey}</span>
            <span className="mb-2 block text-[11px] text-[var(--color-text-muted)]">
              {t.apiKeyHint}
            </span>
            <input
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="••••••••"
              autoComplete="off"
              className={INPUT_CLASS}
            />
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-medium text-[var(--color-text)]">
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-border-strong)] text-indigo-600"
            />
            {t.active}
          </label>
          <p className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
            {t.savingNote}
          </p>
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] pt-4">
            {configured && (
              <button
                type="button"
                onClick={gate.lock}
                className="rounded-md border border-[var(--color-border-strong)] px-4 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
              >
                {t.cancel}
              </button>
            )}
            <button
              type="button"
              disabled={!canSave}
              onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <KeyRound size={13} aria-hidden="true" />
              {save.isPending ? t.saving : current ? t.rotateCredentials : t.saveCredentials}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT(mediaGenerationI18n)
  return (
    <span
      className={
        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ' +
        (active
          ? 'bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] text-[var(--color-success)] ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]'
          : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] ring-[var(--color-border)]')
      }
    >
      {active ? t.active : t.deactivated}
    </span>
  )
}
